import React from "react";
import {
  Code,
  Sparkles,
  Database,
  Bot,
  BarChart3,
  Layers,
  Settings2,
  Zap,
  LayoutDashboard,
  MonitorSmartphone,
} from "lucide-react";

const groupedServices = [
  {
    group: "Web & App Development",
    icon: <Code size={20} />,
    services: [
      {
        title: "Full Stack Web App Development",
        icon: <Code size={28} />,
        description:
          "Optimize your business processes with robust, scalable full stack web applications tailored to your needs—designed for performance, flexibility, and long-term growth.",
      },
    ],
  },
  {
    group: "Web Design & UI/UX",
    icon: <MonitorSmartphone size={20} />,
    services: [
      {
        title: "Interactive Web Experiences",
        icon: <LayoutDashboard size={28} />,
        description:
          "Craft stunning, responsive websites with reusable UI components, modern design systems, and smooth animations—powered by utility-first styling, rich motion effects, and dynamic, interactive interfaces.",
      },
    ],
  },
  {
    group: "AI & Automation",
    icon: <Bot size={20} />,
    services: [
      {
        title: "AI-Powered Business Solutions",
        icon: <Bot size={28} />,
        description:
          "Integrate GPT-4 or custom LLMs like Lumi into your workflows to automate decisions and communication.",
      },
      {
        title: "Process Automation",
        icon: <Zap size={28} />,
        description:
          "Uncover inefficiencies and streamline your operations by automating manual tasks with intelligent triggers and seamless API integrations.",
      },
      {
        title: "Smart Notifications & Event Alerts",
        icon: <Sparkles size={28} />,
        description:
          "Deploy intelligent alert systems that empower your team to stay proactive with real-time, context-aware insights and timely notifications.",
      },
    ],
  },
  {
    group: "Data & Analytics",
    icon: <BarChart3 size={20} />,
    services: [
      {
        title: "Data Analytics & Dashboards",
        icon: <BarChart3 size={28} />,
        description:
          "Make informed decisions and uncover valuable insights with interactive dashboards, detailed summaries, and AI-powered predictive analytics designed to help you deep-dive into your data.",
      },
      {
        title: "Master Data Management",
        icon: <Database size={28} />,
        description:
          "Clean, standardize, and maintain your material master and business-critical data with governance and automation frameworks.",
      },
    ],
  },
  {
    group: "Enterprise Solutions & Business Platforms",
    icon: <Layers size={20} />,
    services: [
      {
        title: "Supply Chain & MRO Optimization",
        icon: <Settings2 size={28} />,
        description:
          "Through LookApp and OpenMRO, we help reduce inventory waste and improve procurement and maintenance strategies.",
      },
      {
        title: "SAP Consulting Services",
        icon: <Layers size={28} />,
        description:
          "Transform your operations with expert SAP consulting services—from implementation and upgrades to optimization, integration, and cloud migration.",
      },
    ],
  },
];

const Services = () => {
  return (
    <section className="min-h-screen bg-[#f5f5f5] dark:bg-slate-900 py-20 px-6 font-[Inter] transition-colors duration-300">
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-[DM Sans] font-bold mb-4 text-[#111] dark:text-white">
          Achieve Success Through Innovation
        </h2>
        <p className="text-gray-600 dark:text-slate-300 mb-12 text-lg max-w-2xl mx-auto">
          Empowering growth with cutting-edge applications, data insights, and integrated ERP solutions
        </p>

        {/* Grouped Services */}
        <div className="space-y-20">
          {groupedServices.map((group, idx) => (
            <div key={idx} className="space-y-10">
              {/* Section Header */}
              <div className="flex items-center mb-8 text-left border-l-4 border-[#111] dark:border-blue-400 pl-4">
                <span className="text-[#111] dark:text-blue-400">{group.icon}</span>
                <h3 className="ml-3 text-2xl md:text-3xl font-bold text-[#111] dark:text-white">
                  {group.group}
                </h3>
              </div>

              {/* Service Cards */}
              <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {group.services.map((service, sIdx) => (
                  <div
                    key={sIdx}
                    className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-blue-700 dark:hover:border-blue-400 hover:-translate-y-1 transition-all duration-300 text-left"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-[#eaeaea] dark:bg-slate-700 rounded-full text-[#111] dark:text-blue-200 mb-4">
                      {service.icon}
                    </div>
                    <h4 className="text-lg font-semibold text-[#111] dark:text-white mb-2">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 dark:text-slate-300 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
